import { OrderLoadStatus } from 'components/Sell/TableLookupDialog/shared/types'
import useFetchApi from 'hooks/useFetchApi/useFetchApi'
import { groupBy, isNil, keyBy, keys, mapValues, values } from 'lodash-es'
import { OrderOwnerContract, OrderLookupContract } from 'models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useBuildOrderItems from '../../../components/Sell/hook/useBuildOrderItems'
import { OrderViewModel } from '../contexts/OrdersContext'
import orderService from 'Services/api/order.service'
import { OrderNotificationContract } from 'hooks/useOrderNotification/OrderNotificationContract'
import { OrderLineNotificationContract } from 'hooks/useOrderNotification/OrderLineNotificationContract'
import { isOrderNotComplete } from 'components/Sell/utils/common'

export const getOrderName = (
    order?: OrderOwnerContract | OrderLookupContract,
) => {
    if (!order) return ''
    if (order.aliasName) return order.aliasName
    if (order.orderNo) return `Bill ${order.orderNo}`
    if (order.tableName) return `Table ${order.tableName}`
    if (order.tableGroupName) return `Table Group ${order.tableGroupName}`
    return `MAIN`
}
export function useOrderDetail({
    relatedOrders = [],
    onLoadOrderSucess,
    loadAllOrderDetails = false,
}: {
    relatedOrders?: OrderOwnerContract[]
    onLoadOrderSucess?: (order: OrderLookupContract) => void
    loadAllOrderDetails?: boolean
}) {
    const { convertToOrderItemViewModels } = useBuildOrderItems()
    const [orders, setOrders] = useState<{
        [orderID: string]: OrderViewModel
    }>({})

    const [, getOrderApi] = useFetchApi(orderService.getOrder)

    const [loadState, setLoadState] = useState<{
        [orderID: string]: OrderLoadStatus
    }>({})

    const [allDoneOrder, setAllDoneOrder] = useState<{
        [orderID: string]: boolean
    }>({})

    const setOrder = useCallback((orderID: string, order: OrderViewModel) => {
        setOrders((prev) => ({
            ...prev,
            [orderID]: order,
        }))
    }, [])

    /** Called whenever child component `EachTableOrderDetail` successfully loaded data from API */
    const handleOnEachOrderLoadSuccess = useCallback(
        (
            orderID: string,
            hasVisibleOrderLine: boolean,
            order?: OrderLookupContract,
        ) => {
            setLoadState((prev) => ({
                ...prev,
                [orderID]: hasVisibleOrderLine
                    ? OrderLoadStatus.LOADED_HAS_DATA
                    : OrderLoadStatus.LOADED_HAS_NO_DATA,
            }))
            // TODO: re-check the caller to find out why order is null in first place
            if (order) {
                setOrder(orderID, {
                    ...order,
                    orderItems: [],
                })
                onLoadOrderSucess?.(order)
            }
        },
        [onLoadOrderSucess, setOrder],
    )

    const handleBillClearStatus = useCallback(
        (orderID: string, isClear: boolean) => {
            setAllDoneOrder((prev) => ({
                ...prev,
                [orderID]: isClear,
            }))
        },
        [],
    )

    const isAllOrdersFinished = useMemo(() => {
        return values(allDoneOrder).every((isDone) => isDone)
    }, [allDoneOrder])

    const checkEveryTableOrderLinePaidOrVoided = useCallback(
        (order?: OrderLookupContract) => {
            if (!handleBillClearStatus || !order?.orderLines) return
            if (order.orderLines.length === 0)
                return handleBillClearStatus(order.orderID!, true)
            if (isOrderNotComplete(order))
                handleBillClearStatus(order.orderID!, false)
            else handleBillClearStatus(order.orderID!, true)
        },
        [handleBillClearStatus],
    )

    const getOrderDetails = useCallback(
        async (orderID: string) => {
            const order = await getOrderApi(orderID)
            const hasVisibleOrderLine = (order?.orderLines ?? []).length > 0
            const relatedOrder = relatedOrders.find(
                (r) => r.orderID === orderID,
            )
            checkEveryTableOrderLinePaidOrVoided(order)
            handleOnEachOrderLoadSuccess(orderID, hasVisibleOrderLine, order)
            const orderModel: OrderViewModel = {
                ...order,
                ...relatedOrder,
                orderItems: convertToOrderItemViewModels(
                    order?.orderLines ?? [],
                ).orderItems,
            }
            setOrder(orderID, orderModel)
            return order!
        },
        [
            checkEveryTableOrderLinePaidOrVoided,
            convertToOrderItemViewModels,
            getOrderApi,
            handleOnEachOrderLoadSuccess,
            setOrder,
            relatedOrders,
        ],
    )

    const loadAllOrders = useCallback(async () => {
        const res = await Promise.all(
            relatedOrders.map((o) => getOrderDetails(o.orderID!)),
        )
        return res
    }, [getOrderDetails, relatedOrders])

    const updateStatusToOrder = useCallback(
        (order: OrderNotificationContract) => {
            const orderID = order.orderID!
            const targetOrder = orders[orderID]
            if (isNil(targetOrder)) return
            setOrder(orderID, {
                ...targetOrder,
                fulfillmentStatus:
                    order.fulfillmentStatus ?? targetOrder.fulfillmentStatus,
                status: order.status ?? targetOrder.status,
                orderPaymentStatus:
                    order.orderPaymentStatus ?? targetOrder.orderPaymentStatus,
            })
        },
        [setOrder, orders],
    )

    const updateKDSStatusToOrderLines = useCallback(
        (orderLines: OrderLineNotificationContract[]) => {
            const groupdOrders = groupBy(values(orderLines), (ol) => ol.orderID)
            keys(groupdOrders).forEach((orderID) => {
                let targetOrder = orders[orderID]
                if (isNil(targetOrder)) return

                let targetOrderLines = targetOrder.orderLines ?? []
                let targetOrderItems = targetOrder.orderItems ?? []

                const updatedItems = values(groupdOrders[orderID])

                updatedItems.forEach((i) => {
                    var orderLineIndex = targetOrderLines.findIndex(
                        (ol) => ol.orderLineID === i.orderLineID,
                    )
                    if (orderLineIndex !== -1)
                        targetOrderLines[orderLineIndex].kdsStatus = i.kdsStatus

                    var orderItemIndex = targetOrderItems.findIndex(
                        (ol) => ol.id === i.orderLineID,
                    )
                    if (orderItemIndex !== -1)
                        targetOrderItems[orderItemIndex].kdsStatus =
                            i.kdsStatus ??
                            targetOrderItems[orderItemIndex].kdsStatus
                })

                var newOrder: OrderViewModel = {
                    ...targetOrder,
                    orderLines: targetOrderLines,
                    orderItems: targetOrderItems,
                }
                setOrder(orderID, newOrder)
            })
        },
        [setOrder, orders],
    )

    //reset all value when relatedOrders changes
    useEffect(() => {
        if (relatedOrders) {
            setOrders(
                mapValues(keyBy(relatedOrders, 'orderID'), () => ({
                    orderItems: [],
                })),
            )
            setAllDoneOrder(
                mapValues(
                    keyBy(relatedOrders, 'orderID'),
                    (i) =>
                        i.isComplete || i.isVoid || !i.hasOrderLines || false,
                ),
            )
        }
    }, [relatedOrders])

    const [hasLoadedOrders, setHasLoadedOrders] = useState(false)

    useEffect(() => {
        if (
            loadAllOrderDetails &&
            relatedOrders.some(
                (i) =>
                    i.orderID &&
                    loadState[i.orderID] === OrderLoadStatus.LOADING,
            ) &&
            !hasLoadedOrders
        ) {
            loadAllOrders().then(() => setHasLoadedOrders(true))
        }
    }, [
        loadAllOrderDetails,
        loadAllOrders,
        relatedOrders,
        loadState,
        hasLoadedOrders,
    ])

    return {
        isAllOrdersFinished,
        allDoneOrder,
        loadState,
        handleBillClearStatus,
        handleOnEachOrderLoadSuccess,
        orders,
        loadAllOrders,
        setOrder,
        getOrderDetails,
        updateStatusToOrder,
        updateKDSStatusToOrderLines,
    }
}
